import "@/styles/design-tokens.css";
import "@/App.css";
import "@/styles/hud-pages.css";
import "@/styles/smooth-compat.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { APIProvider } from "@vis.gl/react-google-maps";
import { AuthProvider } from "./contexts/AuthContext";
import { ChannelProvider } from "./contexts/ChannelContext";
import { MapBackgroundProvider } from "./contexts/MapBackgroundContext";
import MapBackgroundLayer from "./components/MapBackgroundLayer";
import { MapVRAMProvider } from "./lib/MapVRAMGuard";
import { GOOGLE_MAPS_API_KEY } from "./lib/mapsKey";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import HUDLayout from "./components/HUDLayout";
import { AuthOverlay } from "./components/hud";
import OnboardingWizard from "./components/auth/OnboardingWizard";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
// V14.68.2 archived (July 2026): ImageEditorPage — 0 internal links found. voir historique git (src/_archive supprimé V14.84)
// V14.68.17 archived (Feb 2026): TrackingPage, JarvisTeaser — 0 internal usage. voir historique git
import BillingPage from "./pages/BillingPage";
import AdminPage from "./pages/AdminPage";
import PictogramsLabPage from "./pages/admin/PictogramsLabPage";
import GenerationPage from "./pages/GenerationPage";
import ProfilePage from "./pages/ProfilePage";
import PlanPrepPage from "./pages/PlanPrepPage";
import SmartExtractorPage from "./pages/SmartExtractorPage";
import PromptConfigPage from "./pages/PromptConfigPage";
import HubDashboard from "./pages/HubDashboard";
import ExteriorStudioPage from "./pages/exterior/ExteriorStudioPage";
import PatrimoineMapPage from "./pages/patrimoine/PatrimoineMapPage";
import SmartUploadPage from "./pages/SmartUploadPage";
import MaterialsPage from "./pages/MaterialsPage";
import ViewerPage from "./pages/ViewerPage";
import CrmPage from "./pages/CrmPage";
import MaterialThequePage from "./pages/MaterialThequePage";
// V14.68.2 archived (July 2026): PlanCropperDemo + DigitalTwinEditor — 0 internal links found. voir historique git
import StudioDashboardPage from "./pages/studio/StudioDashboardPage";
import StudioFilesPage from "./pages/studio/StudioFilesPage";
import StudioLibraryPage from "./pages/studio/StudioLibraryPage";
import PublicShareView from "./pages/PublicShareView";
import StageStoryPage from "./pages/StageStoryPage";
import ClientViewerPage from "./pages/ClientViewerPage";
import PitchDeckShare from "./pages/PitchDeckShare";
import FloorplanExtractorLab from "./pages/FloorplanExtractorLab";
import SingleHousePage from "./components/maison/SingleHousePage";
import HubAddressesPage from "./pages/HubAddressesPage"; // V14.68.5 — HUB Addresses Phase B

function AppRouter() {
  const location = useLocation();

  // Synchronous session_id detection (prevents race with ProtectedRoute)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <MapBackgroundLayer />
      <AuthOverlay />
      <OnboardingWizard />
      <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/share/:token" element={<PublicShareView />} />
      <Route path="/pitch/:token" element={<PitchDeckShare />} />
      {/* V14.68.17 — /jarvis archived (teaser marketing permanent en preview) */}
      <Route path="/jarvis" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<LoginPage />} />

      {/* Protected routes — toutes en HUDLayout (V1 migrées en sauce HUD V2) */}
      <Route element={
        <ProtectedRoute>
          <HUDLayout />
        </ProtectedRoute>
      }>
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
        {/* V14.68.17 — /tracking archived (aucun usage interne détecté) */}
        <Route path="/tracking" element={<Navigate to="/studio/dashboard" replace />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/pictograms" element={<PictogramsLabPage />} />
        <Route path="/generation" element={<GenerationPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/plan-prep" element={<PlanPrepPage />} />
        <Route path="/extractor" element={<SmartExtractorPage />} />
        <Route path="/prompt-config" element={<PromptConfigPage />} />
        <Route path="/projects/:projectId/prompt-config" element={<PromptConfigPage />} />
        <Route path="/crm" element={<CrmPage />} />
        <Route path="/materialtheque" element={<MaterialThequePage />} />
        {/* V14.68.5 — HUB Adresses Phase B (map-first "Mes lieux") */}
        <Route path="/hub/addresses" element={<HubAddressesPage />} />
        {/* V14.68.2: /studio/plan-cropper-demo + /studio/v2-lab/lot/:lotId archived (unused). voir historique git */}
        {/* V14.1 — Sprint B : nouvelles pages Espace de travail */}
        <Route path="/studio/dashboard" element={<StudioDashboardPage />} />
        <Route path="/hub/carte" element={<HubDashboard embedded={true} />} />
        <Route path="/studio/files"     element={<StudioFilesPage />} />
        <Route path="/studio/library"   element={<StudioLibraryPage />} />
        {/* Sprint Core Engine — POC Floorplan-to-Vector */}
        <Route path="/lab/floorplan-extractor" element={<FloorplanExtractorLab />} />
      </Route>

      {/* Hub V2 — /dashboard fusionné dans /studio/dashboard (V14.68.17).
          L'URL /dashboard est préservée en redirection pour les bookmarks. */}
      <Route path="/dashboard" element={<Navigate to="/studio/dashboard" replace />} />
      <Route path="/hub" element={<Navigate to="/studio/dashboard" replace />} />
      <Route path="/studio-exterieur" element={<ProtectedRoute><ExteriorStudioPage /></ProtectedRoute>} />
      <Route path="/patrimoine" element={<ProtectedRoute><PatrimoineMapPage /></ProtectedRoute>} />
      <Route path="/hub/upload" element={<ProtectedRoute><SmartUploadPage /></ProtectedRoute>} />
      <Route path="/hub/materials/:projectId" element={<ProtectedRoute><MaterialsPage /></ProtectedRoute>} />
      <Route path="/hub/viewer/:projectId" element={<ProtectedRoute><ViewerPage /></ProtectedRoute>} />
      {/* V14.68.5 route moved into HUDLayout group above */}
      {/* /hub/lab removed — Lab page deprecated */}
      <Route path="/hub/lab" element={<Navigate to="/studio/dashboard" replace />} />

      {/* Backward compat — redirect old /dashboard-classic to /projects (V1 reskinned) */}
      <Route path="/dashboard-classic" element={<Navigate to="/projects" replace />} />

      {/* V14.68.2: /editor/:fileId (ImageEditorPage) archived (unused). voir historique git (src/_archive supprimé V14.84) */}

      {/* V14.68.16 (Feb 2026) — /lab/geometry archived. POC Sprint 18 remplacé
          par le Blueprint Aligner + FloorplanExtractorLab. Redirection vers
          la nouvelle page POC pour URL bookmarkées. voir historique git. */}
      <Route path="/lab/geometry" element={<Navigate to="/lab/floorplan-extractor" replace />} />

      {/* V14.62 Sprint 22 — Mode Maison Unifamiliale */}
      <Route path="/maison/:houseId" element={
        <ProtectedRoute>
          <SingleHousePage />
        </ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  // ===== Single Google Maps SDK instance for the whole app =====
  // Lifting APIProvider here guarantees that the SDK loads exactly once with
  // a single set of `version` + `libraries`. Every <Map> in any descendant
  // component reuses this provider, which avoids the "loaded with different
  // parameters" warning AND the D3D11 ShaderResourceView allocation failures
  // that triggered CONTEXT_LOST_WEBGL on lower-end GPUs.
  //
  // EXCEPTION : the public investor pitch deck route (/pitch/:token) is
  // rendered OUTSIDE the providers chain so the Google Maps alpha-version
  // warning banner never pollutes the premium landing page.
  //
  // V14.62 — Maps API key is sourced from `lib/mapsKey.js` (canonical name :
  // REACT_APP_GOOGLE_MAPS_API_KEY, with legacy fallback). NEVER hardcoded.
  const mapsApiKey = GOOGLE_MAPS_API_KEY;
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  if (path.startsWith('/pitch/') || path.startsWith('/story/') || path.startsWith('/viewer/')) {
    return (
      <div className="App min-h-screen" style={{ background: '#0A0A0C' }}>
        <BrowserRouter>
          <Routes>
            <Route path="/pitch/:token" element={<PitchDeckShare />} />
            <Route path="/story/:token" element={<StageStoryPage />} />
            <Route path="/viewer/:token" element={<ClientViewerPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </div>
    );
  }
  return (
    <div className="App min-h-screen bg-gray-50">
      <BrowserRouter>
        <AuthProvider>
          <ChannelProvider>
          <MapBackgroundProvider>
            <MapVRAMProvider>
              <APIProvider apiKey={mapsApiKey} version="alpha" libraries={['maps3d', 'marker', 'places', 'geometry']}>
                <AppRouter />
                <Toaster position="top-right" richColors />
              </APIProvider>
            </MapVRAMProvider>
          </MapBackgroundProvider>
          </ChannelProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
